import React from 'react';
import { Eye, EyeOff, KeyRound, Pencil, Plus, ToggleLeft, ToggleRight, Trash2, UserCog, Wand2 } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { canManageAccount } from '@/lib/constants';
import {
  BottomSheet,
  CardAction,
  ChipRow,
  EmptyState,
  PageHeader,
  Pager,
  PhonePage,
  PillButton,
  PrimaryButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
} from '@/components/phone/primitives';
import { INPUT_CLASS, inputStyle } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';
import { ROLE_LABEL, ROLE_OPTIONS } from './roles';

/**
 * Everyone with a login, on a phone.
 *
 * The desktop grid is eight columns; a card here says who the account is, what
 * they may reach, which stages and lines they cover, and whether it is switched
 * on. The role tabs and the role dropdown collapse into one row of chips, and
 * every row button the grid offers is under the card it belongs to.
 */

function UserCard({ user: u, actor, isAdmin, stageLabel, typeLabel, onEdit, onReset, onToggle, onDelete }) {
  const inactive = (u.status || 'active') === 'inactive';
  const stages = u.linked_stage_ids || [];
  const types = u.linked_product_types || [];
  const contact = [u.mobile_number, u.email].filter(Boolean).join(' · ');

  return (
    <li
      data-testid={`user-card-${u.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <div className="px-4 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>{u.full_name}</p>
            <p className="truncate text-[12px]" style={{ color: Colors.text.tertiary }}>{u.username}</p>
          </div>
          <StatusPill label={inactive ? 'inactive' : 'active'} tone={inactive ? 'neutral' : 'positive'} dot />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <StatusPill label={ROLE_LABEL[u.role] || u.role} tone="accent" />
          {(u.secondary_roles || []).map((r) => <StatusPill key={r} label={ROLE_LABEL[r] || r} />)}
        </div>

        {(stages.length > 0 || types.length > 0 || u.role === 'supervisor') && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {stages.slice(0, 3).map((s) => <StatusPill key={s} label={stageLabel(s)} tone="caution" />)}
            {stages.length > 3 && (
              <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>+{stages.length - 3}</span>
            )}
            {types.length
              ? types.map((c) => <StatusPill key={c} label={typeLabel(c)} tone="accent" />)
              : <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>All types</span>}
          </div>
        )}

        <p className="py-3 text-[12px]" style={{ color: Colors.text.secondary }}>
          {contact || 'No contact on file'}
          {' · '}
          {u.last_login ? `Last in ${formatIST(u.last_login, { compact: true })}` : 'Never signed in'}
        </p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto border-t px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ borderColor: Colors.border.subtle }}>
        {canManageAccount(actor, u) ? (
          <>
            <CardAction icon={Pencil} label="Edit user" onClick={onEdit} testID={`edit-user-${u.id}`} />
            <CardAction icon={KeyRound} label="Reset password" onClick={onReset} testID={`reset-user-${u.id}`} />
            <CardAction
              icon={inactive ? ToggleLeft : ToggleRight}
              label={inactive ? 'Activate user' : 'Deactivate user'}
              tone={inactive ? 'positive' : 'critical'}
              onClick={onToggle}
              testID={`toggle-user-${u.id}`}
            />
            {isAdmin && u.id !== actor?.id && (
              <CardAction icon={Trash2} label="Delete user" tone="critical" onClick={onDelete} testID={`delete-user-${u.id}`} />
            )}
          </>
        ) : (
          <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>admin only</span>
        )}
      </div>
    </li>
  );
}

/** A new password for one account, at the thumb. */
function ResetPasswordSheet({ user, password, setPassword, show, setShow, onGenerate, onSubmit, onClose }) {
  return (
    <BottomSheet open={!!user} onClose={onClose} title="Reset password" subtitle={user ? `${user.full_name} · ${user.username}` : ''} testID="reset-password-sheet">
      <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>New password</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            data-testid="reset-password-input"
            className={`${INPUT_CLASS} pr-11`}
            style={inputStyle}
            type={show ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center"
            style={{ color: Colors.text.tertiary }}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          aria-label="Generate a password that is easy to read out"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] border"
          style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
        >
          <Wand2 className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-2 text-[11px]" style={{ color: Colors.text.tertiary }}>
        Read it to them and ask them to change it after signing in.
      </p>
      <PrimaryButton className="mt-4" onClick={onSubmit} testID="reset-password-submit">Reset password</PrimaryButton>
    </BottomSheet>
  );
}

export default function PhoneUsersList({
  users, total, totalAllRoles, roleCounts, loading, page, totalPages, onPage, onSearch,
  roleFilter, onRole, actor, isAdmin, stageLabel, typeLabel,
  onNew, onEdit, onReset, onToggle, onDelete,
  resetFor, resetPassword, setResetPassword, showResetPassword, setShowResetPassword,
  onGeneratePassword, onResetSubmit, onResetClose,
}) {
  const chips = [
    { key: 'all', label: `All ${totalAllRoles}` },
    ...ROLE_OPTIONS.map((r) => ({
      key: r.value,
      label: roleCounts[r.value] != null ? `${r.label} ${roleCounts[r.value]}` : r.label,
    })),
  ];

  return (
    <PhonePage testID="users-page">
      <PageHeader
        title="Users"
        subtitle={`${total} account${total === 1 ? '' : 's'}`}
        action={(
          <PillButton onClick={onNew} testID="create-user-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Name, username…" onChange={onSearch} testID="users-search-input" />
        <div className="mt-3">
          <ChipRow chips={chips} active={roleFilter || 'all'} onSelect={(key) => onRole(key === 'all' ? null : key)} testIDPrefix="stat" />
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : users.length === 0 ? (
          <EmptyState icon={UserCog} message="No users found" />
        ) : (
          <ul data-testid="users-list">
            {users.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                actor={actor}
                isAdmin={isAdmin}
                stageLabel={stageLabel}
                typeLabel={typeLabel}
                onEdit={() => onEdit(u)}
                onReset={() => onReset(u)}
                onToggle={() => onToggle(u)}
                onDelete={() => onDelete(u)}
              />
            ))}
          </ul>
        )}
        <Pager page={page} totalPages={totalPages} onPage={onPage} testID="users-pagination" />
      </div>

      <ResetPasswordSheet
        user={resetFor}
        password={resetPassword}
        setPassword={setResetPassword}
        show={showResetPassword}
        setShow={setShowResetPassword}
        onGenerate={onGeneratePassword}
        onSubmit={onResetSubmit}
        onClose={onResetClose}
      />
    </PhonePage>
  );
}
